import React, { Fragment } from 'react'
import { observer, inject } from 'mobx-react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'

import Checkbox from '@components/Checkbox'
import Spinner from '@components/spinner'
import fromPairs from 'lodash/fromPairs'

import { formatDate } from '@atheneum/api/dist/lib'

function ExpertPaymentsTable ({ expertPaymentsStore }) {
  const {
    expertPayments,
    toggleAllPayments,
    selectedPayments,
    loading
  } = expertPaymentsStore

  const selectedPaymentsMap = fromPairs(
    selectedPayments.map(p => [p.cpdId, true])
  )

  const allPaymentsSelected =
    !!expertPayments.length && selectedPayments.length === expertPayments.length

  return (
    <Fragment>
      <table className='table-wrapper'>
        <thead>
          <tr>
            <th colSpan='10'>Records ({expertPaymentsStore.count})</th>
          </tr>
        </thead>
        <thead>
          <tr>
            <TableHeader width='3.13%'>
              <Checkbox
                onChange={toggleAllPayments}
                value={allPaymentsSelected}
                disabled={!expertPayments.length}
              />
            </TableHeader>
            <TableHeader
              sortBy='expertFullName'
              expertPaymentsStore={expertPaymentsStore}
              width='12.65%'
            >
              Expert
            </TableHeader>
            <TableHeader
              sortBy='projectName'
              expertPaymentsStore={expertPaymentsStore}
              width='21.80%'
            >
              Project name
            </TableHeader>
            <TableHeader
              sortBy='consultationDate'
              expertPaymentsStore={expertPaymentsStore}
              width='10.17%'
            >
              Consultation date
            </TableHeader>
            <TableHeader
              sortBy='free'
              expertPaymentsStore={expertPaymentsStore}
              width='4.36%'
            >
              Free
            </TableHeader>
            <TableHeader
              sortBy='datePcfReceived'
              expertPaymentsStore={expertPaymentsStore}
              width='10.17%'
            >
              Date EPD received
            </TableHeader>
            <TableHeader
              sortBy='datePaymentExpected'
              expertPaymentsStore={expertPaymentsStore}
              width='8.14%'
            >
              Payment due
            </TableHeader>
            <TableHeader
              sortBy='invoiceNumber'
              expertPaymentsStore={expertPaymentsStore}
              width='9.08%'
            >
              Invoice number
            </TableHeader>
            <TableHeader
              sortBy='paymentStatusId'
              expertPaymentsStore={expertPaymentsStore}
              width='10.17%'
            >
              Payment status
            </TableHeader>
            <TableHeader
              sortBy='expertPaymentDetailStatusId'
              expertPaymentsStore={expertPaymentsStore}
              width='10.32%'
            >
              Payment information
            </TableHeader>
          </tr>
        </thead>
        {!loading && (
          <tbody>
            {expertPayments.map(payment => (
              <ExpertPaymentRow
                selected={!!selectedPaymentsMap[payment.cpdId]}
                key={payment.cpdId}
                data={payment}
                expertPaymentsStore={expertPaymentsStore}
              />
            ))}
          </tbody>
        )}
      </table>
      {loading && <Spinner />}
    </Fragment>
  )
}

ExpertPaymentsTable.propTypes = {
  expertPaymentsStore: PropTypes.object.isRequired
}

export default observer(ExpertPaymentsTable)

const ExpertPaymentRow = inject('sidebarStore')(
  ({ data, selected, expertPaymentsStore, sidebarStore }) => {
    const { togglePayment, staticData } = expertPaymentsStore
    const { paymentStatuses, expertPaymentDetailStatuses } = staticData.names

    const datePaid =
      data.paymentStatusId === staticData.CONST.PAYMENT_STATUSES.PAID
        ? `(${formatDate(data.paymentDate)})`
        : ''
    return (
      <tr
        onClick={() => {
          expertPaymentsStore.loadSidebarData(data)
          sidebarStore.openSidebar({
            template: 'consultation-details',
            expertPaymentsStore
          })
        }}
      >
        <TableData>
          <Checkbox value={selected} onChange={() => togglePayment(data)} />
        </TableData>
        <TableData>
          <Link to={`/expert/${data.expertId}`}>{data.expertFullName}</Link>
        </TableData>
        <TableData>
          <Link to={`/project/${data.projectId}`}>{data.projectName}</Link>
        </TableData>
        <TableData>{formatDate(data.consultationDate)}</TableData>
        <TableData>{data.free ? 'Yes' : 'No'}</TableData>
        <TableData>{formatDate(data.datePcfReceived)}</TableData>
        <TableData>{formatDate(data.datePaymentExpected)}</TableData>
        <TableData>{data.invoiceNumber}</TableData>
        <TableData>
          {paymentStatuses[data.paymentStatusId]} {datePaid}
        </TableData>
        <TableData>
          {expertPaymentDetailStatuses[data.expertPaymentDetailStatusId]}
        </TableData>
      </tr>
    )
  }
)

ExpertPaymentRow.propTypes = {
  data: PropTypes.object.isRequired,
  expertPaymentsStore: PropTypes.object.isRequired,
  selected: PropTypes.bool
}

function TableData ({ children }) {
  return (
    <td>
      <span onClick={e => e.stopPropagation()}>{children}</span>
    </td>
  )
}

TableData.propTypes = {
  children: PropTypes.node
}

function TableHeader ({ children, sortBy, width, expertPaymentsStore }) {
  const style = { width }
  if (!sortBy) {
    return <th style={style}>{children}</th>
  }

  const sortSymbolMap = {
    asc: '▾',
    desc: '▴'
  }

  const { toggleSortOption, loading } = expertPaymentsStore
  const { sortOptions } = expertPaymentsStore.prepareQuery()

  return (
    <th
      style={style}
      className={`sortable-column ${loading ? 'sticky-fix' : ''}`}
      onClick={() => toggleSortOption(sortBy)}
    >
      {children} {sortSymbolMap[sortOptions[sortBy]]}
    </th>
  )
}

TableHeader.propTypes = {
  children: PropTypes.node.isRequired,
  sortBy: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  expertPaymentsStore: PropTypes.object
}
